// Global auth state — single source of truth for who is logged in.
// Access token lives in-memory only (never persisted).
// Refresh token is persisted to localStorage via tokens.ts helpers.

import { create } from 'zustand';

import { API_BASE_URL, AUTH_ENDPOINTS } from '@/constants/api';
import type { AdminUser, TokenRefreshResponse } from '@/types/auth';
import {
  clearAllAuthStorage,
  getStoredAdmin,
  getStoredRefreshToken,
  setStoredAdmin,
  setStoredRefreshToken,
} from '@/lib/auth/tokens';

interface AuthState {
  admin: AdminUser | null;
  accessToken: string | null;
  // Short-lived mfa_challenge token — present only between credentials OK and TOTP verify.
  mfaToken: string | null;
  // Permission codes fetched from the backend after login (e.g. "content.publish").
  // null = not yet fetched. Empty array = fetched but no permissions granted.
  // super_admin bypasses all permission checks regardless of this value.
  permissions: string[] | null;
  isAuthenticated: boolean;
  // True once the app has checked localStorage on first mount.
  isInitialized: boolean;
  isLoading: boolean;

  // ── Actions ──

  /**
   * Call after POST /login succeeds AND GET /auth/me has been fetched.
   * Login flow: POST /login → GET /auth/me → setTokens(access, refresh, fullAdmin)
   */
  setTokens: (accessToken: string, refreshToken: string, admin: AdminUser) => void;
  /** Store permission codes fetched from the backend permissions API. */
  setPermissions: (permissions: string[]) => void;
  setMfaToken: (token: string) => void;
  clearMfaToken: () => void;
  /** Used by the axios interceptor — do not call directly in components. */
  getAccessToken: () => string | null;
  logout: () => Promise<void>;
  /**
   * Exchange the stored refresh token for a new access token.
   * Uses plain fetch (not axios) to avoid a circular import with client.ts.
   * Refresh tokens do NOT rotate — same token remains valid until 7-day expiry.
   */
  refreshAccessToken: () => Promise<string | null>;
  /**
   * Fetch a fresh admin profile from GET /auth/me and update the store + localStorage cache.
   * Call after MFA setup/disable to reflect the updated mfa_enabled flag without a full re-login.
   */
  refreshAdmin: () => Promise<void>;
  /**
   * Run once on app mount: restore session from localStorage if a refresh token exists.
   * Always sets isInitialized = true when done, regardless of outcome.
   */
  initialize: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  admin: null,
  accessToken: null,
  mfaToken: null,
  permissions: null,
  isAuthenticated: false,
  isInitialized: false,
  isLoading: false,

  getAccessToken: () => get().accessToken,

  setTokens: (accessToken, refreshToken, admin) => {
    setStoredRefreshToken(refreshToken);
    setStoredAdmin(admin);
    set({ accessToken, admin, isAuthenticated: true, mfaToken: null });
  },

  setPermissions: (permissions) => set({ permissions }),

  setMfaToken: (token) => set({ mfaToken: token }),
  clearMfaToken: () => set({ mfaToken: null }),

  logout: async () => {
    // Best-effort server revoke — always clear local state even if the request fails.
    const { accessToken } = get();
    if (accessToken) {
      try {
        const { default: apiClient } = await import('@/lib/api/client');
        await apiClient.post(AUTH_ENDPOINTS.LOGOUT);
      } catch {
        // Intentionally swallowed.
      }
    }
    clearAllAuthStorage();
    set({
      admin: null,
      accessToken: null,
      mfaToken: null,
      permissions: null,
      isAuthenticated: false,
    });
  },

  refreshAccessToken: async () => {
    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) return null;

    try {
      const res = await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.REFRESH}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!res.ok) {
        clearAllAuthStorage();
        set({ admin: null, accessToken: null, permissions: null, isAuthenticated: false });
        return null;
      }

      const body = await res.json();
      // Backend wraps in StandardResponse<AdminTokenRefreshResponse>.
      // Refresh tokens do NOT rotate — backend returns only access_token.
      const data: TokenRefreshResponse = body.data ?? body;
      set({ accessToken: data.access_token });
      return data.access_token;
    } catch {
      clearAllAuthStorage();
      set({ admin: null, accessToken: null, permissions: null, isAuthenticated: false });
      return null;
    }
  },

  refreshAdmin: async () => {
    try {
      const { default: apiClient } = await import('@/lib/api/client');
      const freshAdmin = await apiClient.get<never, AdminUser>(AUTH_ENDPOINTS.ME);
      setStoredAdmin(freshAdmin);
      set({ admin: freshAdmin });
    } catch {
      // Silently ignore — stale cached admin data is not critical.
    }
  },

  initialize: async () => {
    set({ isLoading: true });
    try {
      const refreshToken = getStoredRefreshToken();
      if (!refreshToken) return;

      const newToken = await get().refreshAccessToken();
      if (!newToken) return;

      // Show cached profile immediately to avoid a blank flash.
      const storedAdmin = getStoredAdmin();
      if (storedAdmin) {
        set({ admin: storedAdmin, isAuthenticated: true });
      }

      // Always fetch a fresh profile from the server to catch role/status changes.
      try {
        const { default: apiClient } = await import('@/lib/api/client');
        const freshAdmin = await apiClient.get<never, AdminUser>(AUTH_ENDPOINTS.ME);
        setStoredAdmin(freshAdmin);
        set({ admin: freshAdmin, isAuthenticated: true });

        // Fetch permissions for non-super_admin roles.
        // super_admin bypasses all permission checks so no fetch needed.
        if (freshAdmin.role !== 'super_admin') {
          try {
            const data = await apiClient.get<never, { permissions: string[] }>(AUTH_ENDPOINTS.MY_PERMISSIONS);
            set({ permissions: data.permissions });
          } catch {
            // Network issue — permissions stay null.
            // usePermissions conservatively denies non-super_admin checks when null.
          }
        }
      } catch {
        // Network issue — stay authenticated with the cached profile.
      }
    } finally {
      set({ isInitialized: true, isLoading: false });
    }
  },
}));
